import { FormEvent, useState } from 'react'
import { Box, Button, Grid, TextField, Typography } from '@mui/material'
import { emailValidator, phoneNumberValidator } from '../../utils/validators'
import { MarketplaceIdentity } from '../../utils/marketplaceColdStart'

type IdentityFormProps = {
  initial: MarketplaceIdentity
  submitting?: boolean
  onSubmit: (identity: MarketplaceIdentity) => void
}

type Field = keyof MarketplaceIdentity

const LABELS: Record<Field, string> = {
  first_name: 'Prénom',
  last_name: 'Nom',
  email: 'Adresse e-mail',
  mobile_phone: 'N° de mobile',
}

const validate = (identity: MarketplaceIdentity): Partial<Record<Field, string>> => {
  const errors: Partial<Record<Field, string>> = {}
  if (!identity.first_name.trim()) errors.first_name = 'Indiquez votre prénom'
  if (!identity.last_name.trim()) errors.last_name = 'Indiquez votre nom'
  if (!emailValidator(identity.email.trim())) errors.email = 'Adresse e-mail invalide'
  if (!phoneNumberValidator(identity.mobile_phone.trim())) errors.mobile_phone = 'Numéro de mobile invalide'
  return errors
}

/** Coordonnées demandées avant le catalogue (lien `identity=1`), préremplies depuis l'URL quand elle les porte. */
const IdentityForm = ({ initial, submitting = false, onSubmit }: IdentityFormProps) => {
  const [identity, setIdentity] = useState<MarketplaceIdentity>(initial)
  const [errors, setErrors] = useState<Partial<Record<Field, string>>>({})

  const handleChange = (field: Field, value: string) => {
    setIdentity((previous) => ({ ...previous, [field]: value }))
    if (errors[field]) {
      setErrors((previous) => {
        const next = { ...previous }
        delete next[field]
        return next
      })
    }
  }

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    const found = validate(identity)
    setErrors(found)
    if (Object.keys(found).length === 0) {
      onSubmit({
        first_name: identity.first_name.trim(),
        last_name: identity.last_name.trim(),
        email: identity.email.trim(),
        mobile_phone: identity.mobile_phone.trim(),
      })
    }
  }

  const field = (name: Field, props: Record<string, unknown>) => (
    <TextField
      id={`marketplace_${name}`}
      label={LABELS[name]}
      value={identity[name]}
      onChange={(event) => handleChange(name, event.target.value)}
      error={Boolean(errors[name])}
      helperText={errors[name]}
      required
      fullWidth
      {...props}
    />
  )

  return (
    <Box component="form" noValidate onSubmit={handleSubmit} sx={{ maxWidth: 560, mx: 'auto', display: 'grid', gap: 3 }}>
      <Box>
        <Typography variant="h4" component="h1" sx={{ mb: 1 }}>
          Faisons connaissance
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
          Ces informations servent à préparer votre dossier d'inscription : vous n'aurez pas à les saisir de nouveau.
        </Typography>
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          {field('first_name', { inputProps: { autoComplete: 'given-name' } })}
        </Grid>
        <Grid item xs={12} sm={6}>
          {field('last_name', { inputProps: { autoComplete: 'family-name' } })}
        </Grid>
        <Grid item xs={12}>
          {field('email', { type: 'email', inputProps: { autoComplete: 'email', autoCapitalize: 'none' } })}
        </Grid>
        <Grid item xs={12}>
          {field('mobile_phone', { type: 'tel', inputProps: { autoComplete: 'tel', inputMode: 'tel' } })}
        </Grid>
      </Grid>
      <Button type="submit" variant="contained" size="large" disabled={submitting} sx={{ minHeight: 44 }}>
        Voir les formations
      </Button>
    </Box>
  )
}

export default IdentityForm
